/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, Divider, Typography } from "@material-ui/core";
import { DsmButton, DsmIcon } from "@dsm-dcs/design-system-react";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { roiCalculatorStyles, roiResultsStyles } from "../../../ThemeProvider";
import { RoiResultsLocationState } from "../../models/RoiResults";
import { useGetRoiResults } from "../../hooks/useGetRoiResults";
import { getStageOutputTypeLabelId } from "../../helpers/roiStageData";
import {
  formatRoiCurrency,
  formatRoiNumber,
} from "../../helpers/roiFormatters";
import CalculationMethodsModal from "../../components/FarmFlow/RoiCalculator/CalculationMethodsModal";
import ComparisonBarCard from "../../components/FarmFlow/RoiResults/ComparisonBarCard";
import MetricRow from "../../components/FarmFlow/RoiResults/MetricRow";
import { useSubheader } from "../../../../_metronic/layout";

interface RoiResultsRouteParams {
  customerID: string;
  farmID: string;
  processID: string;
  interventionId: string;
}

const SustellRoiResultsPage = () => {
  const history = useHistory();
  const location = useLocation<RoiResultsLocationState | undefined>();
  const intl = useIntl();
  const classes = roiCalculatorStyles();
  const resultsClasses = roiResultsStyles();
  const [methodsModalOpen, setMethodsModalOpen] = useState(false);
  const { customerID, farmID, processID, interventionId } =
    useParams<RoiResultsRouteParams>();
  const subheader = useSubheader();

  const { roiResults, baselineName, interventionName, loading, failed } =
    useGetRoiResults({
      customerId: customerID,
      farmId: farmID,
      interventionReference: interventionId,
    });

  const roiState = location.state;

  useEffect(() => {
    const farmName = roiState?.farmName;
    const processName = roiState?.processName;
    const animalType = roiState?.animalType;
    if (!farmName && !processName) return;
    const crumbs: {
      pathname?: string;
      title: string;
      state?: Record<string, unknown>;
    }[] = [
      {
        pathname: `/customers/${customerID}/myfarms`,
        title: intl.formatMessage({ id: "FARMS.CARD.TITLE" }),
        state: { animalType },
      },
    ];
    if (farmName) {
      crumbs.push({
        pathname: `/customers/${customerID}/farm/${farmID}`,
        title: farmName,
        state: { animalType },
      });
    }
    if (processName) {
      crumbs.push({
        pathname: `/customers/${customerID}/farm/${farmID}/pp/${processID}`,
        title: processName,
        state: { animalType },
      });
    }
    crumbs.push({
      title: intl.formatMessage({ id: "SUSTELL.ROI.RESULTS.TITLE" }),
    });
    subheader.setBreadcrumbs(crumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roiState?.farmName, roiState?.processName]);

  const handleBackToInput = () => {
    history.push(
      `/customers/${customerID}/farm/${farmID}/pp/${processID}/roi/${interventionId}`,
      {
        formData: roiState?.formData,
        farmName: roiState?.farmName,
        processName: roiState?.processName,
        animalType: roiState?.animalType,
      }
    );
  };

  if (loading) {
    return null;
  }

  if (!roiResults) {
    return (
      <Box className={classes.pageContainer}>
        <Box className={resultsClasses.noDataContainer}>
          <Typography className={resultsClasses.noDataMessage}>
            {intl.formatMessage({
              id: failed
                ? "SUSTELL.ROI.LOAD_ERROR"
                : "SUSTELL.ROI.RESULTS.NO_DATA",
            })}
          </Typography>
          <DsmButton
            variant="secondary"
            onClick={() =>
              history.push(
                `/customers/${customerID}/farm/${farmID}/pp/${processID}/roi/${interventionId}`
              )
            }
          >
            <DsmIcon slot="before" name="arrows/arrow-narrow-left" />
            {intl.formatMessage({ id: "GENERAL.BACK" })}
          </DsmButton>
        </Box>
      </Box>
    );
  }

  const { currency } = roiResults;

  const { stageName, outputType } = roiResults.totalClimateChange;
  const outputTypeLabelId = outputType
    ? getStageOutputTypeLabelId(outputType)
    : null;
  const climateChangeStageLabel = [
    stageName,
    outputTypeLabelId ? intl.formatMessage({ id: outputTypeLabelId }) : "",
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <Box className={classes.pageContainer}>
      <CalculationMethodsModal
        open={methodsModalOpen}
        onClose={() => setMethodsModalOpen(false)}
      />

      <Box className={classes.pageHeaderRow}>
        <Box>
          <Typography variant="h6" className={classes.baselineInterventionName}>
            {[baselineName, interventionName].filter(Boolean).join(" | ")}
          </Typography>

          <Typography variant="h4" className={classes.pageTitle}>
            {intl.formatMessage({ id: "SUSTELL.ROI.RESULTS.TITLE" })}
          </Typography>

          <DsmButton
            variant="text"
            style={{
              padding: 0,
              justifyContent: "flex-start",
              marginTop: "12px",
            }}
            onClick={() => setMethodsModalOpen(true)}
          >
            <DsmIcon slot="before" name="general/eye" />
            <Typography className={classes.viewMethodsLink}>
              {intl.formatMessage({ id: "SUSTELL.ROI.RESULTS.VIEW_METHODS" })}
            </Typography>
          </DsmButton>
        </Box>

        <DsmButton variant="secondary" onClick={handleBackToInput}>
          <DsmIcon slot="before" name="layout/list" />
          {intl.formatMessage({ id: "SUSTELL.ROI.RESULTS.VIEW_INPUT_VALUES" })}
        </DsmButton>
      </Box>

      <Divider className={classes.divider} />

      <Box className={resultsClasses.chartsRow}>
        <ComparisonBarCard
          title={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.TOTAL_FEED_COST",
          })}
          description={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.TOTAL_FEED_COST.DESCRIPTION",
          })}
          baseline={roiResults.totalFeedCost.baseline}
          intervention={roiResults.totalFeedCost.intervention}
          formatValue={(value) =>
            formatRoiCurrency(value, intl.locale, currency)
          }
          percentageChange={roiResults.totalFeedCost.percentageChange}
        />

        <Divider
          orientation="vertical"
          flexItem
          className={resultsClasses.chartsRowDivider}
        />

        <ComparisonBarCard
          title={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.TOTAL_CLIMATE_CHANGE",
          })}
          description={
            roiResults.totalClimateChange.available
              ? climateChangeStageLabel
              : intl.formatMessage({ id: "SUSTELL.ROI.RESULTS.NO_FOOTPRINT" })
          }
          baseline={roiResults.totalClimateChange.baseline}
          intervention={roiResults.totalClimateChange.intervention}
          formatValue={(value) => formatRoiNumber(value, intl.locale)}
          valueUnit={roiResults.totalClimateChange.unit}
          percentageChange={roiResults.totalClimateChange.percentageChange}
          available={roiResults.totalClimateChange.available}
        />
      </Box>

      <Divider className={classes.divider} />

      <Box className={resultsClasses.metricsColumn}>
        <MetricRow
          title={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_EXCLUDING_CARBON",
          })}
          label={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_EXCLUDING_CARBON.LABEL",
          })}
          tooltip={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_EXCLUDING_CARBON.TOOLTIP",
          })}
          value={formatRoiCurrency(
            roiResults.roiExcludingCarbon.value,
            intl.locale,
            currency
          )}
          percentageChange={roiResults.roiExcludingCarbon.percentage}
        />

        <MetricRow
          title={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_INCLUDING_CARBON",
          })}
          label={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_INCLUDING_CARBON.LABEL",
          })}
          tooltip={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_INCLUDING_CARBON.TOOLTIP",
          })}
          value={
            roiResults.roiIncludingCarbon.available
              ? formatRoiCurrency(
                  roiResults.roiIncludingCarbon.value,
                  intl.locale,
                  currency
                )
              : undefined
          }
          percentageChange={roiResults.roiIncludingCarbon.percentage}
          unavailableMessage={intl.formatMessage({
            id: "SUSTELL.ROI.RESULTS.ROI_INCLUDING_CARBON.NOT_CALCULATED",
          })}
        />

        {roiResults.isFeedIntervention && (
          <MetricRow
            title={intl.formatMessage({
              id: "SUSTELL.ROI.RESULTS.COST_OF_ABATEMENT",
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.ROI.RESULTS.COST_OF_ABATEMENT.TOOLTIP",
            })}
            value={
              roiResults.costOfAbatement.available
                ? `${formatRoiNumber(
                    roiResults.costOfAbatement.value,
                    intl.locale
                  )} €/kgCO2eq`
                : undefined
            }
            percentageChange={roiResults.totalClimateChange.percentageChange}
            unavailableMessage={intl.formatMessage({
              id: roiResults.costOfAbatement.footprintAvailable
                ? "SUSTELL.ROI.RESULTS.COST_OF_ABATEMENT.NO_ABATEMENT"
                : "SUSTELL.ROI.RESULTS.NO_FOOTPRINT",
            })}
          />
        )}
      </Box>
    </Box>
  );
};

export default SustellRoiResultsPage;
